/**
 * The variant matrix for a curated global — the Studio's grid, from the writing side.
 *
 * Same component, same "mostly empty on purpose": an open cell is an invitation, nothing is
 * stored for one, and the absence *is* the state. What differs is who is standing there. A
 * parent fills cells because they want a pose; an admin fills a few so a **starter arrives
 * with more than one**, which is the difference between a shared shelf that is usable and one
 * that is a list of names.
 *
 * One structural difference, and it is a rule rather than a preference: a global render
 * carries no `requestedBy`, so `variantInstances` will never **list** one. The Studio reads a
 * whole matrix in a single query scoped on the requester; here every cell reads its own
 * content address by id. That is the honest cost of a record that belongs to nobody, and it is
 * why each cell is its own component — one hook each, rather than a loop of hooks.
 */

import {
  Button,
  Dialog,
  Matrix,
  MatrixCell,
  SectionHeader,
  SelectField,
} from '@atlas-nook/design-system';
import { DEFAULT_THEME_ID } from '@atlas-nook/brand';
import { priceOf } from '@atlas-nook/image-worker/cost';
import { matrixPoints, variantInstanceId } from '@atlas-nook/story';
import type { AxisDef, Primitive, Variant } from '@atlas-nook/story';
import { useState } from 'react';
import type { JSX } from 'react';

import { requestRetryCall } from '../data/callables.js';
import { useVariantInstance } from '../data/queries.js';
import { preRender } from '../data/writes.js';
import { useToast } from '../shell/ToastProvider.js';
import { ADMIN_STRINGS as S } from '../strings.js';
import { RenderImage } from './RenderImage.js';

/** The human reading of a point: "sitting · calm". */
function pointLabel(axes: AxisDef[], variant: Variant): string {
  return axes
    .map((axis) => axis.options.find((option) => option.key === variant[axis.key])?.label.en)
    .filter((label): label is string => label !== undefined)
    .join(' · ');
}

export function GlobalMatrix({
  primitive,
  allowed,
}: {
  primitive: Primitive;
  /** False while a character's reference is unapproved: nothing else is drawn until it is. */
  allowed: boolean;
}): JSX.Element {
  const toast = useToast();
  const [asking, setAsking] = useState<Variant | null>(null);
  const [working, setWorking] = useState(false);
  /** Which option each axis beyond the first two is held at. */
  const [held, setHeld] = useState<Variant>({});

  const axes = primitive.axes;
  const [columnAxis, rowAxis, ...rest] = axes;

  if (axes.length === 0 || columnAxis === undefined) {
    return (
      <>
        <SectionHeader title={S.global.matrix} />
        <p className="note">{S.global.axesNone}</p>
      </>
    );
  }

  const heldVariant: Variant = Object.fromEntries(
    rest.flatMap((axis) => {
      const chosen = held[axis.key] ?? axis.options[0]?.key;
      return chosen === undefined ? [] : [[axis.key, chosen]];
    }),
  );

  const columns = columnAxis.options;
  const rows = rowAxis?.options ?? [];

  const points = matrixPoints(axes).filter((variant) =>
    Object.entries(heldVariant).every(([key, value]) => variant[key] === value),
  );

  /** Row-major, in the order `Matrix` lays them out. */
  const ordered = (rows.length === 0 ? [undefined] : rows).flatMap((row) =>
    columns.map((column) =>
      points.find(
        (variant) =>
          variant[columnAxis.key] === column.key &&
          (row === undefined || variant[rowAxis?.key ?? ''] === row.key),
      ),
    ),
  );

  async function draw(variant: Variant): Promise<void> {
    setWorking(true);
    try {
      await preRender({ primitive, variant, themeId: DEFAULT_THEME_ID });
      setAsking(null);
    } catch {
      toast.show(S.common.failed);
    } finally {
      setWorking(false);
    }
  }

  return (
    <>
      <SectionHeader
        title={S.global.matrix}
        meta={
          <span className="rowbar" style={{ gap: 'var(--sp-3)' }}>
            <span>
              {rowAxis === undefined ? columnAxis.key : `${columnAxis.key} × ${rowAxis.key}`}
            </span>
            {rest.map((axis) => (
              <span key={axis.key} className="heldaxis">
                <span className="cap">{axis.key}</span>
                <SelectField
                  compact
                  labelHidden
                  label={S.global.heldAxis(axis.key)}
                  value={heldVariant[axis.key] ?? ''}
                  onChange={(option) => setHeld((was) => ({ ...was, [axis.key]: option }))}
                >
                  {axis.options.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label.en}
                    </option>
                  ))}
                </SelectField>
              </span>
            ))}
          </span>
        }
      />

      <p className="note" style={{ maxWidth: '66ch' }}>
        {allowed ? S.global.matrixHint : S.global.matrixBlocked}
      </p>

      <Matrix
        label={
          rowAxis === undefined
            ? S.global.matrixLabel(primitive.name.en, columnAxis.key)
            : S.global.matrixLabelTwo(primitive.name.en, columnAxis.key, rowAxis.key)
        }
        columns={columns.map((option) => option.label.en)}
        rows={rows.map((option) => option.label.en)}
      >
        {ordered.map((variant, index) =>
          variant === undefined ? (
            <span key={index} role="presentation" />
          ) : (
            <Cell
              key={index}
              primitive={primitive}
              variant={variant}
              allowed={allowed}
              onDraw={() => setAsking(variant)}
            />
          ),
        )}
      </Matrix>

      <Dialog
        open={asking !== null}
        onClose={() => setAsking(null)}
        title={
          asking === null
            ? ''
            : S.global.drawTitle(`${primitive.name.en} · ${pointLabel(axes, asking)}`)
        }
        cost={S.cost.oneGeneration(`$${priceOf('1K').toFixed(4)}`)}
        actions={
          <>
            <Button tone="quiet" onClick={() => setAsking(null)}>
              {S.common.cancel}
            </Button>
            <Button
              tone="primary"
              working={working}
              onClick={() => asking !== null && void draw(asking)}
            >
              {S.global.drawConfirm}
            </Button>
          </>
        }
      >
        <p className="note">{S.global.drawBody}</p>
      </Dialog>
    </>
  );
}

/**
 * One cell, and one listener.
 *
 * Its own component precisely so the hook count is fixed: a loop of `useDocument` calls over a
 * changing point list would break the rules of hooks the first time an axis gained an option.
 */
function Cell({
  primitive,
  variant,
  allowed,
  onDraw,
}: {
  primitive: Primitive;
  variant: Variant;
  allowed: boolean;
  onDraw: () => void;
}): JSX.Element {
  const id = variantInstanceId(primitive.id, primitive.version, variant, DEFAULT_THEME_ID);
  const { data: instance } = useVariantInstance(id);

  const name = primitive.name.en;
  const point = pointLabel(primitive.axes, variant);

  if (instance?.status === 'ready') {
    return (
      <MatrixCell state="filled" label={S.global.cellFilled(name, point)}>
        <RenderImage path={instance.assetPath} alt={`${name}, ${point}`} />
      </MatrixCell>
    );
  }

  if (instance?.status === 'pending' || instance?.status === 'generating') {
    return (
      <MatrixCell state="making" label={S.global.cellMaking(name, point)} verb={S.global.making} />
    );
  }

  if (instance?.status === 'error') {
    return (
      <MatrixCell
        state="error"
        label={S.global.cellError(name, point)}
        verb={S.global.failed}
        hint={S.global.tryAgain}
        onClick={() => void requestRetryCall(instance.id)}
      />
    );
  }

  return (
    <MatrixCell
      state="open"
      label={allowed ? S.global.cellOpen(name, point) : S.global.cellBlocked(name, point)}
      verb={point.split(' · ')[0]}
      hint={S.global.tapToDraw}
      disabled={!allowed}
      onClick={onDraw}
    />
  );
}
